<template>
  <div>
    <ViewLoadingMessage :loading="!isReady"/>
    <EventTutorial ref="eventTutorial" @completed="onTutorialCompleted"
                   v-if="showWelcomeToPica"/>

    <WelcomeToPica ref="welcomeToPica" @create="openCreateNewEvent"
                   v-if="showWelcomeToPica"/>

    <CRow v-else-if="isReady">
      <CCol lg="12">
        <CCard class="no-borders">
          <CCardHeader>
            <div class="d-flex align-items-center">
              <h1 class="main-header mr-4" v-translate translate-context="events.list">Events</h1>
            </div>
            <div class="card-header-actions">
              <CButton @click.prevent="openCreateNewEvent" v-if="canCreateEvent" color="primary">
                <CIcon name="cip-plus" style="vertical-align: sub"/>
                <translate translate-context="events.list">New event</translate>
              </CButton>
            </div>
          </CCardHeader>

          <CCardBody class="px-0">
            <AjaxTable
                ref="eventsTable"
                :fields="fields"
                :data="events"
                :fetch="fetchEventList"
                :custom-classes="'custom-table custom-table--has-calendar'"
                table-name="events"
                :sync-url="true"
            >
              <template #name="{ item }">
                <td>
                  <CLink :to="routeToDetail(item)" style="color: #100F23">
                    <div class="d-flex align-items-center">
                      <div class="d-flex align-items-center">
                        <CIcon name="cipDot"
                               size="custom-size"
                               :height="10"
                               class="text-danger mr-2"
                               v-c-tooltip="{placement:'top-center', content:$gettext('closed'), appendToBody:true}"
                               v-if="resortIsClosed(item)"/>

                        <CIcon name="cip-globe" class="text-primary v-align-sub mr-2" width="20"
                               v-if="item.event_owner && item.event_owner.third_level_domain"/>
                      </div>
                      <div class="font-weight-semi-bold">{{ item.event.name }}</div>
                    </div>
                  </CLink>
                </td>
              </template>

              <template #event_start_at="{ item }">
                <td>
                  <template v-if="item.event.start_at">
                    {{ item.event.start_at|formatDate }}
                    <div class="tz" v-if="hasDifferentTimezone(item.event.timezone)">
                      {{ item.event.timezone }}
                    </div>
                  </template>
                </td>
              </template>

              <template #status="{ item }">
                <td>
                  <CBadge class="badge-outline" color="light">
                    <CIcon name="cipDot" size="custom-size" :height="6" style="vertical-align: middle"
                           :class="item.status ? 'text-warning' : 'text-success'"/>
                    <span class="ml-1">{{
                        item.status ? $gettext('Draft') : $gettext('Active')
                      }}</span>
                  </CBadge>
                </td>
              </template>

              <template #contract="{ item }">
                <td>
                  <template v-if="item.contract">
                    {{ item.contract.name }}
                  </template>
                </td>
              </template>

              <template #contract_manager="{ item }">
                <td>
                  <template v-if="item.contract_manager">
                    {{ item.contract_manager.name }}
                  </template>
                </td>
              </template>

              <template #actions="{ item }">
                <td class="py-2 text-center sticky-column">
                  <CButton
                      color="dark"
                      variant="outline"
                      class="btn-circle btn-circle--sm"
                      size="sm"
                      :to="routeToDetail(item)">
                    <CIcon name="cipArrowRight"/>
                  </CButton>
                </td>
              </template>
            </AjaxTable>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>


<script>
import ViewLoadingMessage from "@/components/ViewLoadingMessage.vue"
import {mapActions, mapGetters} from "vuex";
import WelcomeToPica from "@/domain/eventWizard/components/WelcomeToPica.vue";
import EventTutorial from "@/domain/events/components/EventTutorial.vue";
import AjaxTable from "@/domain/core/components/AjaxTable.vue";

export default {
  name: 'EventList',
  components: {
    AjaxTable,
    EventTutorial,
    WelcomeToPica,
    ViewLoadingMessage
  },

  data() {
    return {
      isReady: false,
    }
  },

  async mounted() {
    this.isReady = true
  },

  watch: {
    // When welcome screen becomes visible, open the tutorial
    async showWelcomeToPica(newVal) {
      if (newVal) {
        await this.$nextTick()
        if (this.$refs.eventTutorial) {
          this.openTutorial()
        }
      }
    }
  },

  computed: {
    ...mapGetters('events', ['events']),
    ...mapGetters('userStatus', {userStatusLoaded: 'loaded', isSaasUser: 'isSaasUser', hasSaasEvents: 'hasSaasEvents'}),
    ...mapGetters('event', ['resortCategoryId']),
    ...mapGetters('login', ['userTimeZone']),
    canCreateEvent() {
      return this.hasPerm('events.create')
    },
    totalResults() {
      return this.events.pagination.total
    },
    statusFilterOptions() {
      return [
        {value: null, label: this.$gettext('Any')},
        {value: 'true', label: this.$gettext('Draft')},
        {value: 'false', label: this.$gettext('Active')},
      ]
    },
    hasLoadedEvents() {
      // Check if we've actually fetched events (page > 0 means API responded)
      return this.events.pagination.page > 0
    },
    showWelcomeToPica() {
      if (!this.isReady) return false
      if (!this.userStatusLoaded) return false
      if (!this.isSaasUser) return false
      if (this.hasSaasEvents) return false
      return true
    },
    fields() {
      const fields = []

      if (this.hasPerm('events.fields.id'))
        fields.push({key: 'id', label: 'ID', _style: 'width:90px;', sorter: true})

      fields.push({key: 'name', label: this.$gettext('Name'), sorter: true})
      fields.push({key: 'event_start_at', label: this.$gettext('Start on'), sorter: true, dateRangeFrom: 'start_at_from', dateRangeTo: 'start_at_to'})
      fields.push({key: 'status', label: this.$gettext('Status'), sorter: true, options: this.statusFilterOptions})

      if (this.hasPerm('events.fields.is_promo'))
        fields.push({key: 'is_promo', label: 'Promo', boolean: true, sorter: true})

      if (this.hasPerm('events.fields.is_saas'))
        fields.push({key: 'is_saas', label: 'Saas', boolean: true, sorter: true})

      if (this.hasPerm('events.fields.contract'))
        fields.push({key: 'contract', label: this.$gettext('Contract'), sorter: true})

      if (this.hasPerm('events.fields.contract_manager'))
        fields.push({key: 'contract_manager', label: this.$gettext('Contract manager'), sorter: true})

      fields.push({key: 'actions', label: '', sorter: false, filter: false, _style: 'width:70px;', _classes: 'sticky-column'})

      return fields
    }
  },

  methods: {
    ...mapActions('events', [
      'fetchEventList',
    ]),
    routeToDetail(item) {
      return {name: 'picaServiceRedirect', params: {picaServiceId: item['id']}}
    },
    resortIsClosed(item) {
      if (item.event.category_id === this.resortCategoryId) {
        const today = new Date()
        const eventStartAt = new Date(item.event.start_at)
        const eventEndAt = new Date(item.event.end_at)

        if (eventEndAt || (!eventStartAt && eventEndAt)) {
          return today > eventEndAt
        }

        if (eventStartAt && !eventEndAt) {
          return today < eventStartAt
        }

        if (!eventStartAt && !eventEndAt) {
          return true
        }
      }
      return false
    },
    hasDifferentTimezone(timezone) {
      if (!timezone) return false
      return timezone !== this.userTimeZone
    },
    openCreateNewEvent() {
      const parent = this.getParentWithMethod('openCreateNewEvent')
      if (parent) parent.openCreateNewEvent({
        canClose: !this.showWelcomeToPica,
        isFirstEvent: this.totalResults === 0,
        contract: null
      })
      else this.notifyError({text: 'Parent not found'})
    },
    openTutorial() {
      this.$refs.eventTutorial.open()
    },
    async onTutorialCompleted() {
      this.openCreateNewEvent()
    }
  }
}
</script>
